import { apiFetch } from "@/lib/http/apiClient";
import { ApiError } from "@/lib/http/ApiError";
import { z } from "zod";

export interface AuthUser {
  username: string;
  email: string;
  codiceFiscale: string;
  nome: string;
  cognome: string;
  cliente: string;
  nome_cliente: string;
}

export type AuthUnavailableReason = "network" | "server";

export type AuthSnapshot =
  | { status: "pending"; user: null }
  | { status: "unauthenticated"; user: null }
  | { status: "unavailable"; reason: AuthUnavailableReason; user: null }
  | { status: "authenticated"; user: AuthUser | null };

export type RefreshResult =
  | { status: "authenticated" }
  | { status: "unauthenticated" }
  | { status: "unavailable"; reason: AuthUnavailableReason };

export interface LoginCredentials {
  tenant: string;
  usernameOrEmail: string;
  password: string;
}

const userProfileSchema = z.object({
  username: z.string(),
  email: z.string(),
  codiceFiscale: z.string(),
  nome: z.string(),
  cognome: z.string(),
  cliente: z.string(),
  nome_cliente: z.string()
});

type UserProfileDto = z.infer<typeof userProfileSchema>;

let snapshot: AuthSnapshot = { status: "pending", user: null };

const listeners = new Set<() => void>();

const setSnapshot = (next: AuthSnapshot): void => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const setUnauthenticated = (): void => setSnapshot({ status: "unauthenticated", user: null });

const setUnavailable = (reason: AuthUnavailableReason): void =>
  setSnapshot({ status: "unavailable", reason, user: null });

let initPromise: Promise<void> | null = null;
let refreshPromise: Promise<RefreshResult> | null = null;
let sessionGeneration = 0;

export const authStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot(): AuthSnapshot {
    return snapshot;
  },

  /** Bootstraps auth state from the httpOnly refresh cookie, if any. Idempotent/safe to call repeatedly. */
  initialize(): Promise<AuthSnapshot> {
    if (snapshot.status === "authenticated" || snapshot.status === "unauthenticated") {
      return Promise.resolve(snapshot);
    }

    initPromise ??= authStore
      .silentRefresh()
      .then(() => undefined)
      .finally(() => {
        initPromise = null;
      });
    return initPromise.then(() => snapshot);
  },

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    const operationGeneration = ++sessionGeneration;
    const dto = await apiFetch<UserProfileDto>({
      method: "POST",
      path: "/auth/login",
      body: credentials,
      skipCsrf: true,
      skipAuthRetry: true,
    });

    const parsed = userProfileSchema.safeParse(dto);
    if (!parsed.success) {
      throw new ApiError("Login returned an invalid response", 200, z.treeifyError(parsed.error));
    }

    if (operationGeneration !== sessionGeneration) {
      throw new DOMException("Login was superseded by another auth operation", "AbortError");
    }

    setSnapshot({ status: "authenticated", user: parsed.data });
    return parsed.data;
  },

  async logout(): Promise<void> {
    sessionGeneration += 1;
    setUnauthenticated();
    await apiFetch<void>({
      method: "POST",
      path: "/auth/logout",
      skipAuthRetry: true,
    });
  },

  /**
   * Raw, non-recursive refresh call shared by `initialize()` and the http client's 401 retry.
   * Concurrent callers are deduplicated onto a single in-flight request.
   */
  silentRefresh(): Promise<RefreshResult> {
    const operationGeneration = sessionGeneration;
    refreshPromise ??= (async () => {
      try {
        const dto = await apiFetch<UserProfileDto>({
          method: "POST",
          path: "/auth/refresh",
          skipCsrf: true,
          skipAuthRetry: true,
        });

        if (operationGeneration !== sessionGeneration) {
          return { status: "unauthenticated" };
        }

        const parsed = userProfileSchema.safeParse(dto);
        setSnapshot({
          status: "authenticated",
          user: parsed.success ? parsed.data : snapshot.status === "authenticated" ? snapshot.user : null,
        });
        return { status: "authenticated" };
      } catch (error) {
        if (operationGeneration !== sessionGeneration) {
          return { status: "unauthenticated" };
        }

        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setUnauthenticated();
          return { status: "unauthenticated" };
        }

        if (error instanceof ApiError) {
          setUnavailable("server");
          return { status: "unavailable", reason: "server" };
        }

        setUnavailable("network");
        return { status: "unavailable", reason: "network" };
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  },
};
